import React from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import isUndefined from 'lodash/isUndefined';
import sortBy from 'lodash/sortBy';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  InputLabel,
  TextField,
  FormControl,
  FormLabel,
  Grid,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import ErrorMessage from '../../common/error-message';
import MultiSelect from '../../common/multi-select';
import targetDeploymentActions from '../../../actions/targetDeployments';
import * as snackbarActions from '../../../actions/snackbar';

function hasOverlappingTargets(tz, targetDeployments, newTarget) {
  const overlap = targetDeployments.find((target) => {
    const targetDate = moment.utc(newTarget.effectiveOn).tz(tz);
    const newTargetDate = moment.utc(target.effectiveOn).tz(tz);
    return targetDate.isSame(newTargetDate, 'day');
  });

  return !isUndefined(overlap);
}

class BaseCreateTargetDeployment extends React.Component {
  constructor(props) {
    super(props);
    const { market } = props;

    this.state = {
      marketId: market._id,
      effectiveOn: moment.tz(market.tz).format(),
      target: 100,
      fleetId: undefined,
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleCountChange = this.handleCountChange.bind(this);
    this.onSave = this.onSave.bind(this);
  }

  onSave() {
    const {
      createTargetDeployment, fetchTargetDeployments,
      currentTargets, market, markets, handleSave,
      showSuccessSnackbar, showErrorSnackbar,
    } = this.props;
    const {
      _id, marketId, target, effectiveOn, fleetId,
    } = this.state;
    const targetDeploymentMarketId = fleetId ?? marketId;
    const childMarkets = markets.filter((m) => m?.parentMarketId === market._id);
    const targetDeploymentMarket = markets.find((m) => m._id === targetDeploymentMarketId);

    if (!target || target < 1) {
      return this.setState({ errorMessage: 'Please enter a positive number for target quantity.' });
    }
    if (!effectiveOn) {
      return this.setState({ errorMessage: 'Please enter an effective date.' });
    }

    if (!fleetId && childMarkets.length > 0) {
      return this.setState({ errorMessage: 'Please select a fleet.' });
    }

    if (hasOverlappingTargets(targetDeploymentMarket.tz, currentTargets, { target, effectiveOn })) {
      return this.setState({ errorMessage: 'Targets cannot be set for overlapping days.' });
    }

    createTargetDeployment({
      _id, marketId, target, effectiveOn, fleetId,
    })
      .then(() => {
        handleSave();
        showSuccessSnackbar('Successfully saved new target deployment');
        fetchTargetDeployments(market._id);
      })
      .catch((e) => {
        showErrorSnackbar('Error saving target deployment', e);
      });
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value.format(), errorMessage: false });
  }

  handleCountChange(event) {
    const count = parseFloat(event.target.value, 10);
    this.setState({ [event.target.name]: count, errorMessage: false });
  }

  render() {
    const { open, handleClose, editing, market, markets } = this.props;
    const { target, effectiveOn, errorMessage, fleetId } = this.state;

    const childMarkets = markets.filter((m) => m?.parentMarketId === market._id);
    const marketsArr = childMarkets.concat(market);
    const sortedMarkets = sortBy(marketsArr, (
      market,
    ) => market.name.toLowerCase());
    const marketOptions = sortedMarkets.map((sortedMarket) => ({
      value: sortedMarket._id,
      label: sortedMarket.name,
    }));

    return (
      <div>
        <Dialog
          open={open}
          onClose={handleClose}
        >
          <DialogContent>
            <Grid container>
              {market?.extId && (
                <Grid item xs={12} sx={{ mb: 2 }}>
                  <InputLabel sx={{ pb: '2%' }}>Fleet</InputLabel>
                  <MultiSelect
                    placeholder="Select a Fleet"
                    isSingle
                    handleChange={(newMarketId) => this.setState({ fleetId: newMarketId })}
                    value={[fleetId]}
                    options={marketOptions}
                  />
                </Grid>
              )}
              <Grid item xs={12} sx={{ mb: 2 }}>
                <InputLabel>
                  Quantity
                </InputLabel>
                <TextField
                  margin="dense"
                  name="target"
                  type="number"
                  variant="standard"
                  value={target || ''}
                  InputProps={{ inputProps: { min: 1 } }}
                  error={target < 1 || Number.isNaN(target)}
                  fullWidth
                  onChange={this.handleCountChange}
                />
              </Grid>
              <Grid item xs={12} sx={{ mb: 2 }}>
                <FormControl component="fieldset" style={{ width: '100%' }}>
                  <FormLabel component="legend">Start Date</FormLabel>
                  <DatePicker
                    value={moment(effectiveOn)}
                    name="effectiveOn"
                    disabled={editing}
                    format="MMMM Do"
                    autoOk
                    onChange={(newDate) => this.handleChange({ target: { name: 'effectiveOn', value: newDate } })}
                  />
                </FormControl>
              </Grid>
              {errorMessage
              && (
                <Grid item>
                  <ErrorMessage
                    errorMessage={errorMessage}
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={this.onSave} color="primary">
              Save
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
BaseCreateTargetDeployment.defaultProps = {
  currentTargets: [],
};
BaseCreateTargetDeployment.propTypes = {
  handleClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  market: PropTypes.shape({
    tz: PropTypes.string,
  }).isRequired,
  currentTargets: PropTypes.arrayOf(PropTypes.object),
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...targetDeploymentActions,
  ...snackbarActions,
}, dispatch);

const mapStateToProps = () => ({});

const CreateTargetDeployment = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(BaseCreateTargetDeployment));
export { BaseCreateTargetDeployment, CreateTargetDeployment };
